/**
 * Tauchgänge und Stammdaten zwischen Geräten abgleichen.
 *
 * Eine zweite Datei neben `tauchlogbuch-checkin.json`: Der Check-in ändert
 * sich selten und trägt Geheimtext, die Tauchgänge ändern sich oft. Getrennte
 * Dateien übertragen nur, was sich wirklich geändert hat.
 *
 * Ohne Messpunkte — sie machen 95 Prozent der Datenmenge aus und ändern sich
 * nie. Sie kommen auf jedem Gerät einmalig über das Bundle.
 */

import { baueAbzug } from './abzug.js';
import { SPEICHER } from './db.js';
import { grabsteine, loesche, speichere } from './saetze.js';
import { fuehreZusammen } from './sync.js';

export const DATEINAME = 'tauchlogbuch-daten.json';
export const FORMAT = 'tauchlogbuch-sync/1';

/** Die Bereiche, die zwischen Geräten wandern. */
export const BEREICHE = ['dives', 'sites', 'trips', 'buddies', 'gear'];

/** Welche Tabelle zu welchem Bereich gehört. */
const SPEICHER_ZU = {
  dives: SPEICHER.dives,
  sites: SPEICHER.sites,
  trips: SPEICHER.trips,
  buddies: SPEICHER.buddies,
  gear: SPEICHER.gear,
};

export async function eigenerDatenstand(db) {
  const abzug = await baueAbzug(db, { mitProfilen: false });
  const stand = { grabsteine: await grabsteine(db) };
  for (const bereich of BEREICHE) stand[bereich] = abzug[bereich] ?? [];
  return stand;
}

/**
 * Einen Abgleich durchführen: lesen, zusammenführen, lokal schreiben, ablegen.
 *
 * Schlägt das Lesen fehl, bleibt der lokale Bestand unangetastet — es wird
 * nichts halb geschrieben.
 */
export async function gleicheDatenAb(db, adapter, optionen = {}) {
  const fern = await adapter.lies();
  const lokal = await eigenerDatenstand(db);

  if (!fern) {
    await adapter.schreib({ format: FORMAT, erzeugt: new Date().toISOString(), ...lokal });
    return { bericht: null, erstmalig: true };
  }

  const zusammen = fuehreZusammen(lokal, fern, BEREICHE);

  for (const bereich of BEREICHE) {
    const vorhanden = (lokal[bereich] ?? []);
    const neuOderGeaendert = zusammen[bereich].filter((satz) => {
      const eigener = vorhanden.find((s) => s.id === satz.id);
      return !eigener || eigener.geaendert !== satz.geaendert;
    });
    if (neuOderGeaendert.length) {
      await speichere(db, SPEICHER_ZU[bereich], neuOderGeaendert, optionen);
    }
    const weg = vorhanden
      .map((s) => s.id)
      .filter((id) => !zusammen[bereich].some((s) => s.id === id));
    if (weg.length) await loesche(db, SPEICHER_ZU[bereich], weg);
  }

  await adapter.schreib({
    format: FORMAT,
    erzeugt: new Date().toISOString(),
    grabsteine: zusammen.grabsteine,
    ...Object.fromEntries(BEREICHE.map((b) => [b, zusammen[b]])),
  });
  return { bericht: zusammen.bericht, erstmalig: false };
}
